import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

// Celebratory "money + stars raining down" particles — the same effect as a
// "Tip the DJ" tap (see MoodSelector). Reused for the prize-draw win modal.
const TIP_BURST_EMOJIS = ["💵", "💰", "⭐", "✨", "🌟", "🤑", "💸", "🌠"];

const makeTipBurst = () =>
  Array.from({ length: 28 }, (_, id) => ({
    id,
    emoji: TIP_BURST_EMOJIS[Math.floor(Math.random() * TIP_BURST_EMOJIS.length)],
    left: Math.random() * 100,             // vw
    delay: Math.random() * 0.45,           // s
    duration: 2.2 + Math.random() * 1.8,   // s (half speed)
    drift: (Math.random() * 2 - 1) * 60,   // px horizontal drift
    size: 18 + Math.random() * 22,         // px
    rotate: (Math.random() * 2 - 1) * 420, // deg
  }));

/**
 * Fire a money/stars rain by changing `fireKey` to a new truthy value
 * (e.g. an incrementing counter). Each change spawns one burst that clears
 * itself after the animation. Renders nothing when idle.
 */
export default function TipBurst({ fireKey, zIndex = 99998 }) {
  const [burst, setBurst] = useState(null);

  useEffect(() => {
    if (!fireKey) return undefined;
    setBurst(makeTipBurst());
    const t = setTimeout(() => setBurst(null), 5200);
    return () => clearTimeout(t);
  }, [fireKey]);

  if (!burst) return null;

  return createPortal(
    <div
      aria-hidden="true"
      style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex, overflow: "hidden" }}
    >
      {burst.map((p) => (
        <span
          key={p.id}
          style={{
            position: "absolute",
            top: "-8vh",
            left: `${p.left}vw`,
            fontSize: p.size,
            willChange: "transform, opacity",
            animation: `tipRain ${p.duration}s cubic-bezier(0.35,0,0.7,1) ${p.delay}s forwards`,
            "--tip-drift": `${p.drift}px`,
            "--tip-rot": `${p.rotate}deg`,
          }}
        >
          {p.emoji}
        </span>
      ))}
      <style>{`
        @keyframes tipRain {
          0%   { transform: translate(0, -10vh) rotate(0deg); opacity: 0; }
          12%  { opacity: 1; }
          88%  { opacity: 1; }
          100% { transform: translate(var(--tip-drift), 112vh) rotate(var(--tip-rot)); opacity: 0; }
        }
      `}</style>
    </div>,
    document.body
  );
}
